import React, { useState, useEffect } from 'react';
import { Portal } from '../Portal/Portal';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import dangerIcon from '../../assets/icons/danger-icon.svg';
import './TagSaleModal.scss';
import uploadIcon from '../../assets/icons/upload-icon.svg';
import { useTagSubmission } from '../../hooks/useTagSubmission';

interface TagSaleModalProps {
  isOpen: boolean;
  onClose: () => void;
  userData: {
    companyInformations: {
      companyName: string;
    };
  };
}

interface FileWithPreview extends File {
  preview?: string;
}

interface SaleFormData {
  workingForCompany: boolean;
  orderNumber: string;
  clientName: string;
  deliveryAddress: string;
  attachments: FileWithPreview[];
}

export const TagSaleModal: React.FC<TagSaleModalProps> = ({ isOpen, onClose, userData }) => {
  const { t, i18n } = useTranslation('home');
  const [showThankYou, setShowThankYou] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState(false);
  const [formData, setFormData] = useState<SaleFormData>({
    workingForCompany: true,
    orderNumber: '',
    clientName: '',
    deliveryAddress: '',
    attachments: [],
  });

  const tagSubmission = useTagSubmission();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate total file size before submission
    const totalSize = formData.attachments.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_FILE_SIZE) {
      const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
      setError(t('tagSale.errors.totalSizeTooLarge', { totalSize: totalSizeMB, maxSize: '35' }));
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await tagSubmission.mutateAsync({
        orderNumber: formData.orderNumber,
        clientName: formData.clientName,
        deliveryAddress: formData.deliveryAddress,
        files: formData.attachments
      });

      if (response.status === 201) {
        setShowThankYou(true);
      } else {
        setError(response.data?.message || t('tagSale.errorGeneric'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('tagSale.errorGeneric'));
      console.error('Tag submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const createPreviewUrl = (file: File): string => {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    // Return appropriate icon based on file type
    if (file.name.endsWith('.pdf')) {
      return '/icons/pdf-icon.svg';
    } else if (file.name.match(/\.(doc|docx)$/i)) {
      return '/icons/doc-icon.svg';
    } else if (file.name.match(/\.(xls|xlsx)$/i)) {
      return '/icons/xls-icon.svg';
    }
    return '/icons/file-icon.svg';
  };

  const MAX_FILES = 5;
  const MAX_FILE_SIZE = 35 * 1024 * 1024; // 35MB in bytes

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const remainingSlots = MAX_FILES - formData.attachments.length;
      if (remainingSlots <= 0) {
        setError(t('tagSale.errors.tooManyFiles', { max: MAX_FILES }));
        e.target.value = '';
        return;
      }

      const newFiles = Array.from(e.target.files)
        .slice(0, remainingSlots)
        .filter(file => {
          if (file.size > MAX_FILE_SIZE) {
            setError(t('tagSale.errors.fileTooLarge', { filename: file.name, maxSize: '35' }));
            return false;
          }
          return true;
        })
        .map(file => {
          const preview = createPreviewUrl(file);
          return Object.assign(file, { preview });
        });

      if (newFiles.length > 0) {
        // Check if adding these files would exceed total size limit
        const currentTotalSize = formData.attachments.reduce((sum, file) => sum + file.size, 0);
        const newFilesSize = newFiles.reduce((sum, file) => sum + file.size, 0);
        const totalSize = currentTotalSize + newFilesSize;
        
        if (totalSize > MAX_FILE_SIZE) {
          const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
          setError(t('tagSale.errors.totalSizeTooLarge', { totalSize: totalSizeMB, maxSize: '35' }));
          e.target.value = '';
          return;
        }

        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, ...newFiles]
        }));
        setError(null);
      }
      e.target.value = ''; // Reset input to allow uploading same file again
    }
  };

  const handleRemoveFile = (index: number) => {
    setFormData(prev => {
      // Revoke the URL to prevent memory leaks
      if (prev.attachments[index]?.preview) {
        URL.revokeObjectURL(prev.attachments[index].preview!);
      }
      return {
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index),
      };
    });
  };

  // Cleanup object URLs when component unmounts or when modal closes
  useEffect(() => {
    return () => {
      formData.attachments.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, []);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).map(file => {
      const preview = URL.createObjectURL(file);
      return Object.assign(file, { preview });
    });
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files],
    }));
  };

  // Handle scroll lock and cleanup
  useEffect(() => {
    if (isOpen) {
      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
      // Reset form when modal closes
      formData.attachments.forEach(file => {
        if (file.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(file.preview);
        }
      });
      setFormData({
        workingForCompany: true,
        orderNumber: '',
        clientName: '',
        deliveryAddress: '',
        attachments: [],
      });
      setShowThankYou(false);
      setError(null);
    }

    // Cleanup when component unmounts
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isOpen]);

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      formData.attachments.forEach(file => {
        if (file.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, []);

  if (!isOpen) return null;

  if (showThankYou) {
    return (
      <Portal>
        <div className="modal-overlay">
          <div className="modal thank-you-modal">
            <h2>{t('tagSale.thankYou.title')}</h2>
            <p>{t('tagSale.thankYou.message')}</p>
            <div className="modal-actions">
              <button className="button-secondary" onClick={onClose}>
                {t('tagSale.buttons.close')}
              </button>
              <button 
                className="button-primary"
                onClick={() => {
                  // Clean up any existing preview URLs
                  formData.attachments.forEach(file => {
                    if (file.preview?.startsWith('blob:')) {
                      URL.revokeObjectURL(file.preview);
                    }
                  });
                  
                  setShowThankYou(false);
                  setFormData({
                    workingForCompany: true,
                    orderNumber: '',
                    clientName: '',
                    deliveryAddress: '',
                    attachments: [],
                  });
                  setError(null);
                }}
              >
                {t('tagSale.buttons.tagNewSale')}
              </button>
            </div>
          </div>
        </div>
      </Portal>
    );
  }

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal tag-sale-modal" onClick={(e) => e.stopPropagation()}>
          <h2>{t('tagSale.title')}</h2>
          <p className="subtitle">{t('tagSale.subtitle')}</p>

          <form onSubmit={handleSubmit}>
            {!formData.workingForCompany && (
              <div className="warning-message">
                <img src={dangerIcon} alt="warning" className="warning-icon" />{' '}
                {i18n.language === 'fr' ? (
                  <span>
                    Veuillez mettre à jour vos{' '}
                    <Link to="/profile" onClick={onClose}>informations d'entreprise</Link>{' '}
                    avant d'étiqueter une vente
                  </span>
                ) : (
                  <span>
                    Please update your{' '}
                    <Link to="/profile" onClick={onClose}>company information</Link>{' '}
                    before tagging a sale
                  </span>
                )}
              </div>
            )}

            <div className="form-group">
              <p className="company-question">{t('tagSale.companyQuestion1')} <strong>{userData.companyInformations.companyName}</strong>?</p>
              <div className="radio-group">
                <div className="radio-option">
                  <input
                    type="radio"
                    id="yes"
                    name="workingForCompany"
                    checked={formData.workingForCompany}
                    onChange={() => setFormData(prev => ({ ...prev, workingForCompany: true }))}
                  />
                  <label htmlFor="yes">{t('tagSale.answers.yes')}</label>
                </div>
                <div className="radio-option">
                  <input
                    type="radio"
                    id="no"
                    name="workingForCompany"
                    checked={!formData.workingForCompany}
                    onChange={() => setFormData(prev => ({ ...prev, workingForCompany: false }))}
                  />
                  <label htmlFor="no">{t('tagSale.answers.no')}</label>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="orderNumber">{t('tagSale.orderNumber')}</label>
              <input
                type="text"
                id="orderNumber"
                value={formData.orderNumber}
                onChange={e => setFormData(prev => ({ ...prev, orderNumber: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label htmlFor="clientName">{t('tagSale.clientName')}*</label>
              <input
                type="text"
                id="clientName"
                required
                value={formData.clientName}
                onChange={e => setFormData(prev => ({ ...prev, clientName: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label htmlFor="deliveryAddress">{t('tagSale.deliveryAddress')}*</label>
              <p className="helper-text">{t('tagSale.addressFormat')}</p>
              <input
                type="text"
                id="deliveryAddress"
                required
                value={formData.deliveryAddress}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  setFormData(prev => ({ ...prev, deliveryAddress: value }));
                  // Validate address format
                  const parts = value.split(',').map(part => part.trim());
                  const isValid = parts.length === 4 && parts.every(part => part.length > 0);
                  setAddressError(!isValid);
                }}
                className={addressError ? 'error' : ''}
              />
              {addressError && (
                <p className="error-message">{t('tagSale.addressFormatError')}</p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="file-upload">{t('tagSale.attachments.title')}</label>
              <p className="attachment-types">{t('tagSale.attachments.types')}</p>
              <div 
                className={`file-upload-zone ${formData.attachments.length > 0 ? 'has-attachments' : ''}`}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                {formData.attachments.length > 0 ? (
                  <div className="file-preview-grid">
                    {formData.attachments.map((file, index) => (
                      <div key={index} className="file-preview-item">
                        <div className="preview-container">
                          <img 
                            src={file.preview} 
                            alt={file.name} 
                            className="file-preview"
                          />
                          <button
                            type="button"
                            className="remove-file"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFile(index);
                            }}
                            aria-label={t('tagSale.attachments.remove')}
                          >
                            ×
                          </button>
                        </div>
                        <span className="file-name">{file.name}</span>
                      </div>
                    ))}
                    {formData.attachments.length < MAX_FILES && (
                      <div className="upload-more">
                        <img src={uploadIcon} alt="Upload more" />
                        <p>{t('tagSale.attachments.dragDrop')}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <img src={uploadIcon} alt="Upload" />
                    <p>{t('tagSale.attachments.dragDrop')}</p>
                  </>
                )}
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            {error && (
              <div className="error-banner-bottom">
                <img src={dangerIcon} alt="error" className="error-icon" />
                <span>{error}</span>
              </div>
            )}

            <div className="modal-actions">
              <button type="button" className="button-secondary" onClick={onClose}>
                {t('tagSale.buttons.cancel')}
              </button>
              <button 
                type="submit" 
                className="button-primary"
                disabled={isSubmitting || !formData.workingForCompany}
              >
                {isSubmitting ? t('tagSale.buttons.submitting') : t('tagSale.buttons.tagSale')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
};
